/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module CoreModule
 */

import {
    Inject,
    Injectable,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import { StateService } from '@uirouter/core';
import { Store } from '@ngrx/store';
import {
    Observable,
    Subject,
} from 'rxjs';
import { take } from 'rxjs/operators';

import {
    ALBServicesRegistrationStatus,
    IALBServicesStatus,
    ILicenseTierUsage,
    ISaasLicensingStatus,
    LicenseTierType,
} from 'generated-types';

import { EXTERNAL_CLOUD_SERVICES } from 'state-names';
import * as SystemInfoSelectors from 'ng/root-store/system-info/system-info.selectors';
import { LicensingService } from 'ajs/modules/licensing';
import { CportalService } from 'ajs/modules/cportal/services/cportal.service';
import { portalStatus } from 'ajs/modules/cportal/constants';
import { AppLevelAlertsService } from '../app-level-alerts.service';

import * as l10n from './central-license-alerts.service.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

const PULSE_REGISTRATION_ALERT_ID = 'pulse-registration-alert';
const LICENSE_ALERTS_LOCAL_STORAGE_KEY = 'license-alerts';
const LICENSE_ENFORCEMENT_ALERT_ID = 'license-ledger-alert';

/**
 * Enterprise with cloud services license subscription failed alert id.
 */
export const LICENSE_SUBSCRIPTION_FAILED_ALERT_ID = 'license-subscription-fail-alert';

/**
 * @desc Service to fetch app level alerts related to central licensing.
 *
 * @author Nisar Nadaf
 */
@Injectable()
export class CentralLicenseAlertsService {
    /**
     * Get keys from source bundles for template usage
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * Holds controller registration status.
     */
    public controllerRegistrationStatus: ALBServicesRegistrationStatus;

    /**
     * List of alerts closed/dismissed by user.
     */
    private removedAlerts: string[] = [];

    /**
     * Holds enterprise with cloud services license(SaaS) tier subscription status.
     */
    private saasSubscriptionStatus: ISaasLicensingStatus;

    /**
     * Used to track changes in enterprise with cloud services license(SaaS) tier
     * subscription status from reconcile api.
     */
    private readonly licenseSubscriptionStatusSubject = new Subject<void>();

    private isEssentialTier: boolean;

    constructor(
        private readonly appLevelAlertsService: AppLevelAlertsService,
        private readonly cportalService: CportalService,
        private readonly l10nService: L10nService,
        private readonly licensingService: LicensingService,
        @Inject('$state')
        private readonly $state: StateService,
        private readonly store: Store,
    ) {
        l10nService.registerSourceBundles(dictionary);
        this.store.select(SystemInfoSelectors.selectDefaultLicenseTier).subscribe(
            (tier: LicenseTierType): void => {
                if (tier) {
                    this.isEssentialTier = tier === LicenseTierType.ESSENTIALS;
                }
            },
        );
    }

    /**
     * Render app level alerts based on response of api calls.
     * Currently fetching controller registration status.
     */
    public loadAlerts(): void {
        this.setRemovedAlerts();
        this.cportalService.loadRegistrationStatus()
            .then(() => {
                const albserviceStatus = this.cportalService.getRegistrationStatusDetails();

                // Set controller registration and method call to set
                // SaaS status and show license subscription failed app level alert.
                const { registration_status: registrationStatus } = albserviceStatus;

                this.controllerRegistrationStatus = registrationStatus;

                this.setSaasStatusAndRefreshLicenseFailAlert();
            });
    }

    /**
     * Checks Essential tier eligibility based on license ledger essential usage available data,
     * and enforcement date to render app level alert if eligibility conditions are met.
     */
    public async addEssentialsTierLicenseAlert(essentialsEnforcedAt: string): Promise<void> {
        const { tier_usages: tierUsages } = await this.cportalService.loadLicenseLedger();

        if (this.removedAlerts.includes(LICENSE_ENFORCEMENT_ALERT_ID)) {
            return;
        }

        if (this.validateEssentialTierLicenseAlert(tierUsages, essentialsEnforcedAt)) {
            this.appLevelAlertsService.add({
                id: LICENSE_ENFORCEMENT_ALERT_ID,
                componentProps: {
                    messages: [{
                        message: this.l10nService.getMessage(
                            l10nKeys.licenseEnforcementAlertMessage,
                            [essentialsEnforcedAt],
                        ),
                    }],
                    onClose: () => this.dismissAlert(LICENSE_ENFORCEMENT_ALERT_ID),
                },
            });
        } else {
            this.removeAlert(LICENSE_ENFORCEMENT_ALERT_ID);
        }
    }

    /**
     * Checks eligibility criteria for the Essential tier based on usage data and enforcement date.
     * Returns true if the Essential tier's usage availability is 0 from license ledger API,
     * and if the enforced date for Essential tier exceeds the current date from license usage API.
     */
    // eslint-disable-next-line class-methods-use-this
    public validateEssentialTierLicenseAlert(
        tierUsages: ILicenseTierUsage[],
        essentialsEnforcedAt: string,
    ): boolean {
        const ledgerEssentialTierCheck = tierUsages?.some(
            tierDetails => tierDetails?.tier === LicenseTierType.ESSENTIALS &&
                tierDetails?.usage?.available === 0,
        );

        const essentialsEnforcedAtCheck = new Date(essentialsEnforcedAt) > new Date();

        return essentialsEnforcedAtCheck && ledgerEssentialTierCheck;
    }

    /**
     * Setter for license subscription status.
     */
    public set saasStatus(saasStatus: ISaasLicensingStatus) {
        this.saasSubscriptionStatus = saasStatus;
    }

    /**
     * Getter for license subscription status.
     */
    public get saasStatus(): ISaasLicensingStatus {
        return this.saasSubscriptionStatus;
    }

    /**
     * Returns an observable to allow subscriptions to license subscription status change.
     */
    public get licenseSubscriptionStatus$(): Observable<void> {
        return this.licenseSubscriptionStatusSubject.asObservable();
    }

    /**
     * Method to show/hide app level alert and retry button when
     * enterprise with cloud services license tier subscription failed.
     */
    public refreshLicenseSubscriptionFailAlert(): void {
        if (!this.saasStatus) {
            this.removeAlert(LICENSE_SUBSCRIPTION_FAILED_ALERT_ID);

            return;
        }

        const { enabled, message } = this.saasStatus;

        if (this.isEnterpriseWithCloudServicesTier && this.isControllerRegistered && !enabled) {
            // Remove existing alert if present to avoid duplicate ID error
            this.removeAlert(LICENSE_SUBSCRIPTION_FAILED_ALERT_ID);

            this.appLevelAlertsService.add({
                id: LICENSE_SUBSCRIPTION_FAILED_ALERT_ID,
                componentProps: {
                    messages: [{ message }],
                    status: 'danger',
                    closable: false,
                    actionLabel: this.l10nService.getMessage(
                        l10nKeys.retryNowButtonText,
                    ),
                    onClick: () => this.refreshLicenseStatus(),
                },
            });
        } else {
            this.removeAlert(LICENSE_SUBSCRIPTION_FAILED_ALERT_ID);
        }
    }

    /**
     * Render/Remove app level alert based on pulse registration status.
     * Once alert is closed/dismissed by user then it will not re-render.
     */
    public refreshPulseRegistrationAlert(albserviceStatus: IALBServicesStatus): void {
        const { registration_status: registrationStatus } = albserviceStatus;
        const { ALBSERVICES_DEREGISTERED } = portalStatus;
        let defaultLicenseTier: LicenseTierType;

        this.store.select(SystemInfoSelectors.selectDefaultLicenseTier)
            .pipe(take(1))
            .subscribe(tier => defaultLicenseTier = tier);

        // If alert is previously closed by user then don't render it again.
        if (this.removedAlerts.includes(PULSE_REGISTRATION_ALERT_ID)) {
            return;
        }

        // Default license tier is ENTERPRISE_WITH_CLOUD_SERVICES and Controller is not registered
        if (defaultLicenseTier === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES &&
            registrationStatus === ALBSERVICES_DEREGISTERED
        ) {
            this.appLevelAlertsService.add({
                id: PULSE_REGISTRATION_ALERT_ID,
                componentProps: {
                    messages: [{
                        message: this.l10nService.getMessage(
                            l10nKeys.controllerNotRegisteredMessage,
                        ),
                    }],
                    actionLabel: this.l10nService.getMessage(
                        l10nKeys.registerControllerButtonText,
                    ),
                    onClick: () => this.redirectToCloudServices(),
                    onClose: () => this.dismissAlert(PULSE_REGISTRATION_ALERT_ID),
                },
            });
        } else {
            this.removeAlert(PULSE_REGISTRATION_ALERT_ID);
        }
    }

    /**
     * Clears removedAlerts list from localStorage.
     */
    public clearAlertsFromLocalStorage(): void {
        localStorage.removeItem(LICENSE_ALERTS_LOCAL_STORAGE_KEY);
    }

    /**
     * Loads SaaS status and call method to show license subscription failed app level alert.
     */
    public async setSaasStatusAndRefreshLicenseFailAlert(): Promise<void> {
        try {
            const { saas_status: saasStatus } = await this.licensingService.loadLicenseStatus();

            this.saasStatus = saasStatus;
        } catch (error) {
            console.error(error);
        }
    }

    /**
     * Sets removedList data if value present in localStorage.
     */
    private setRemovedAlerts(): void {
        const removedList = localStorage.getItem(LICENSE_ALERTS_LOCAL_STORAGE_KEY);

        this.removedAlerts = [];

        if (removedList) {
            this.removedAlerts = removedList.split(',');
        }
    }

    /**
     * Redirects to the Cloud services settings page.
     */
    private redirectToCloudServices(): void {
        this.$state.go(EXTERNAL_CLOUD_SERVICES);
    }

    /**
     * Dismiss app level alert by given alert ID and store id in removedAlerts.
     */
    private dismissAlert(alertId: string): void {
        this.removeAlert(alertId);
        this.removedAlerts.push(alertId);
        localStorage.setItem(LICENSE_ALERTS_LOCAL_STORAGE_KEY, this.removedAlerts.join(','));
    }

    /**
     * Remove app level alert if present.
     */
    private removeAlert(alertId: string): void {
        if (this.appLevelAlertsService.has(alertId)) {
            this.appLevelAlertsService.remove(alertId);
        }
    }

    /**
     * Returns true if controller is registered.
     */
    private get isControllerRegistered(): boolean {
        const { ALBSERVICES_REGISTERED } = portalStatus;

        return this.controllerRegistrationStatus === ALBSERVICES_REGISTERED;
    }

    /**
     * Returns true for ENTERPRISE_WITH_CLOUD_SERVICES License tier.
     */
    private get isEnterpriseWithCloudServicesTier(): boolean {
        let defaultLicenseTier: LicenseTierType;

        this.store.select(SystemInfoSelectors.selectDefaultLicenseTier)
            .pipe(take(1))
            .subscribe(tier => defaultLicenseTier = tier);

        return defaultLicenseTier === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES;
    }

    /**
     * Call Reconcile api to refresh ENTERPRISE_WITH_CLOUD_SERVICES license subscription status.
     */
    private async refreshLicenseStatus(): Promise<void> {
        try {
            const { saas_status: saasStatus } = await this.licensingService.refreshLicenseStatus();
            const { enabled } = saasStatus;

            if (enabled) {
                this.licenseSubscriptionStatusSubject.next();

                this.removeAlert(LICENSE_SUBSCRIPTION_FAILED_ALERT_ID);
            }
        } catch (error) {
            console.error(error);
        }
    }
}
